import * as Yup from 'yup';
import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Stack, Button } from '@mui/material';
// components
import { FormProvider } from '../../../../components/hook-form';
import { CarePlanConditionToOpportunity } from 'src/_mock/settings/_careplan-automation';
import RHFList from 'src/components/hook-form/RHFList';
import api from 'src/services/api';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// ----------------------------------------------------------------------

type FormValue = {
  conditionIds: CarePlanConditionToOpportunity['condition']['id'][];
  opportunityIds: CarePlanConditionToOpportunity['opportunity']['id'][];
};

const schema = Yup.object().shape({
  opportunityIds: Yup.array().of(Yup.string()).min(1, 'At least one opportunity is required'),
  conditionIds: Yup.array().of(Yup.string()).min(1, 'At least one condition is required'),
});

const resolver = yupResolver(schema);

export default function ConditionsForm() {
  const navigate = useNavigate();
  const { data: conditions = [] } = useQuery(
    ['conditions'],
    async () =>
      (await api.carePlan.findConditions({
        status: '',
      })) as any[]
  );

  const { data: opportunities = [] } = useQuery(
    ['opportunities'],
    async () =>
      (await api.carePlan.findOpportunities({
        status: '',
      })) as any[]
  );

  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () =>
      ({
        conditionIds: [],
        opportunityIds: [],
      } as FormValue),
    []
  );

  const methods = useForm({ resolver, defaultValues });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValue) => {
    try {
      await api.carePlan.createMappingsConditionOpportunity(data);

      reset();
      enqueueSnackbar('Create success!');
      navigate('..');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        enqueueSnackbar(
          error.response?.data?.message?.[0] || error?.message || 'Something went wrong!',
          {
            variant: 'error',
          }
        );
      }

      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack
        gap={3}
        direction="row"
        justifyContent="center"
        sx={{
          p: 3,
        }}
      >
        <RHFList
          name="conditionIds"
          label="Conditions"
          items={conditions.map((condition) => ({
            value: condition.uuid,
            label: condition.name,
          }))}
        />

        <RHFList
          name="opportunityIds"
          label="Opportunities"
          items={opportunities.map((opportunity) => ({
            value: opportunity.uuid,
            label: opportunity.name,
          }))}
        />
      </Stack>

      <Stack direction="row" justifyContent="flex-end" sx={{ p: 3 }} gap={2}>
        <Button variant="text" color="inherit" component={RouterLink} to="..">
          Cancel
        </Button>
        <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
          Create Mapping
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
